import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Price } from '@artosera/ui';
import { messageErreurSaisie } from '../../lib/erreursSaisie.js';
import pageStyles from '../../styles/page.module.css';

/** La langue d'affichage des montants et des dates : `fr` ou `en`. */
export function useLangue(): 'fr' | 'en' {
  const { i18n } = useTranslation('console');
  return i18n.language.startsWith('en') ? 'en' : 'fr';
}

/** Le message d'une erreur de l'API, dans la langue courante (voir `messageErreurSaisie`). */
export function useMessageErreur(ressource?: string): (erreur: unknown) => string {
  const { t } = useTranslation('console');
  return (erreur) => messageErreurSaisie(t, erreur, ressource);
}

/** Un montant saisi, ou « à saisir » : jamais « 0 € » pour ce qui n'est pas saisi. */
export function CelluleMontant({ centimes }: { centimes: number | null }): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  if (centimes === null) return <Badge tone="reserved">{t('saisies.aSaisir')}</Badge>;
  return <Price cents={centimes} lang={langue} />;
}

const LOCALES = { fr: 'fr-FR', en: 'en-GB' } as const;

/** `2026-09-17T00:00:00.000Z` → « 17 sept. 2026 ». UTC : une date saisie n'a pas d'heure. */
export function formaterJour(valeur: string | null, langue: 'fr' | 'en', absent = '—'): string {
  if (!valeur) return absent;
  return new Date(valeur).toLocaleDateString(LOCALES[langue], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** `2026-09` → « septembre 2026 ». */
export function formaterMois(mois: string, langue: 'fr' | 'en'): string {
  const [annee, numero] = mois.split('-').map(Number);
  if (!annee || !numero) return mois;
  return new Date(Date.UTC(annee, numero - 1, 1)).toLocaleDateString(LOCALES[langue], {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** L'état de chargement d'un onglet : « Chargement… » ou l'erreur, jamais un tableau faux. */
export function EtatOnglet({
  chargement,
  enErreur,
}: {
  chargement: boolean;
  enErreur: boolean;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  if (enErreur) {
    return (
      <p className={pageStyles.videEtat} role="alert">
        {t('saisies.erreurChargement')}
      </p>
    );
  }
  if (chargement) {
    return (
      <p className={pageStyles.videEtat} role="status">
        {t('saisies.chargement')}
      </p>
    );
  }
  return null;
}

/** `2026-09-30T18:42:00Z` → « 30 sept. 2026, 20:42 » : date et heure de Paris. */
export function formaterDateHeure(valeur: string, langue: 'fr' | 'en'): string {
  return new Date(valeur).toLocaleString(LOCALES[langue], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
}

/** Le mois d'aujourd'hui au fuseau Europe/Paris, `AAAA-MM` : celui de l'API. */
export function moisCourantParis(maintenant: Date = new Date()): string {
  const parties = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(maintenant);
  const annee = parties.find((partie) => partie.type === 'year')?.value ?? '';
  const mois = parties.find((partie) => partie.type === 'month')?.value ?? '';
  return `${annee}-${mois}`;
}
